// START IMPORTS
import { useAccount, useSession } from '@ltvco/refresh-lib/v1';
import { useState, useEffect } from 'react';
// END IMPORTS

/** Fallback seat cap when a plan has no explicit `max_seats`. */
export const DEFAULT_MAX_SEATS = 1;

export function useLimitedPlanInfo() {
  // START HOOKS_AND_QUERIES
  const {
    session: { isAuthenticated },
  } = useSession();
  const { data: account, isLoading, isError } = useAccount(isAuthenticated);
  // END HOOKS_AND_QUERIES

  // START STATE_MANAGEMENT
  /** Flag to indicate if the account data has been received and loaded */
  const [accountLoaded, setAccountLoaded] = useState(false);

  // Subscription Info
  const [planAmount, setPlanAmount] = useState(0);
  const [planMonthlyCost, setPlanMonthlyCost] = useState(0);
  const [renewalPeriod, setRenewalPeriod] = useState(0);
  const [renewalPeriodType, setRenewalPeriodType] = useState('');
  const [nextRenewalDate, setNextRenewalDate] = useState('');
  const [reportLimit, setReportLimit] = useState(-1);
  const [reportsRemaining, setReportsRemaining] = useState(-1);
  const [subscriptionState, setSubscriptionState] = useState('');
  const [planName, setPlanName] = useState('');

  // Flags and calculated values
  const [isAnnualSubscription, setIsAnnualSubscription] = useState(false);
  const [isCancelledSubscription, setIsCancelledSubscription] = useState(false);
  const [isExpiredSubscription, setIsExpiredSubscription] = useState(false);
  const [isSuspendedSubscription, setIsSuspendedSubscription] = useState(false);
  const [isLimitedUser, setIsLimitedUser] = useState(false);
  const [isStaff, setIsStaff] = useState(false);
  const [isUnlimitedUser, setIsUnlimitedUser] = useState(false);
  const [reportsRunPercentage, setReportsRunPercentage] = useState(0);
  const [reportsRun, setReportsRun] = useState(0);
  // END STATE_MANAGEMENT

  // START EFFECTS
  useEffect(() => {
    if (account) {
      const accountInfo = account.account;

      setAccountLoaded(true);

      setReportLimit(accountInfo.subscription_info.monthly_report_limit);
      setReportsRemaining(
        accountInfo.subscription_info.monthly_reports_remaining
      );
      setNextRenewalDate(accountInfo.subscription_info.normalize_date);
      setPlanAmount(
        parseFloat(accountInfo.subscription_info.money_amount.replace('$', ''))
      );
      setRenewalPeriod(accountInfo.subscription_info.renewal_period);
      setRenewalPeriodType(accountInfo.subscription_info.renewal_period_type);
      setSubscriptionState(accountInfo.subscription_info.subscription_state);
      setIsStaff(
        accountInfo.subscription_info.subscription_plan_internal_name ===
          'internal_staff'
      );
      setPlanName(accountInfo.subscription_info?.subscription_plan_unique_name);
    }
  }, [account]);

  useEffect(() => {
    setIsLimitedUser(reportLimit !== -1);
    setIsUnlimitedUser(reportLimit === -1);
    setReportsRun(reportLimit - reportsRemaining);
  }, [reportLimit, reportsRemaining]);

  useEffect(() => {
    if (reportLimit === -1) {
      return;
    }

    let progress = (reportsRun / reportLimit) * 100;

    if (progress > 100) {
      progress = 100;
    }

    setReportsRunPercentage(progress);
  }, [reportLimit, reportsRun]);

  useEffect(() => {
    if (!planAmount || !renewalPeriod) {
      return;
    }

    setPlanMonthlyCost(planAmount / renewalPeriod);
  }, [planAmount, renewalPeriod]);

  useEffect(() => {
    setIsAnnualSubscription(renewalPeriod === 12);
  }, [renewalPeriod]);

  useEffect(() => {
    setIsCancelledSubscription(
      subscriptionState?.toLowerCase() === 'cancelled'
    );
    setIsExpiredSubscription(subscriptionState?.toLowerCase() === 'expired');
    setIsSuspendedSubscription(
      subscriptionState?.toLowerCase() === 'suspended'
    );
  }, [subscriptionState]);
  // END EFFECTS

  const isSeat = account?.account?.user_info?.is_seat ?? false;

  const subscriptionFeatures =
    account?.account?.subscription_info?.subscription_features;
  const businessAccount = subscriptionFeatures?.business_account ?? false;
  const maxSeats = subscriptionFeatures?.max_seats ?? DEFAULT_MAX_SEATS;
  const isSeatManager = !isSeat && businessAccount;

  const seatStatus = account?.account?.user_info?.seat_status ?? null;
  const noncompliantParent = seatStatus?.['noncompliant_parent?'] ?? false;
  const seatCanRunReports = seatStatus?.seat_can_run_reports ?? true;
  const reportsBlockedByParent = seatStatus?.reports_blocked_by_parent ?? false;
  const seatReportsPaused = isSeat && reportsBlockedByParent;

  // START RENDER
  return {
    accountLoaded,
    isError,
    isAnnualSubscription,
    isCancelledSubscription,
    isExpiredSubscription,
    isSuspendedSubscription,
    isLimitedUser,
    isUnlimitedUser,
    isStaff,
    isSeat,
    businessAccount,
    maxSeats,
    isSeatManager,
    reportsBlockedByParent,
    seatReportsPaused,
    noncompliantParent,
    seatCanRunReports,
    planAmount,
    planMonthlyCost,
    renewalPeriod,
    renewalPeriodType,
    nextRenewalDate,
    reportLimit,
    reportsRun,
    reportsRemaining,
    reportsRunPercentage,
    subscriptionState,
    isLoading,
    planName,
  };
  // END RENDER
}
